import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Option, Select, Sheet,
    Stack, Tab, TabList, TabPanel, Tabs, Typography, tabClasses,
} from '@mui/joy';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import { useAdvertiser } from '../../../AdvertiserContext';
import {
    APIAdsCampaigns, APIAdsReportAudience, APIAdsReportCampaigns,
    APIAdsReportCreatives, APIAdsReportExport, APIAdsReportGeo,
    APIAdsReportPlacements, APIAdsReportSummary, APIAdsReportTimeline,
    adsErrorText, saveBlob,
} from '../../../api_ads';
import AdBreakdownTable from '../../../components/Ads/reports/AdBreakdownTable';
import AdLineChart from '../../../components/Ads/reports/AdLineChart';
import AdMetricCard from '../../../components/Ads/reports/AdMetricCard';
import AdPeriodPicker from '../../../components/Ads/reports/AdPeriodPicker';
import {
    CAMPAIGN_STATUS_LABELS, FORMAT_LABELS, formatInt, formatMoney,
} from '../../../components/Ads/adsConstants';


const GENDER_LABELS = { 0: 'Мужчины', 1: 'Женщины', 2: 'Пары МЖ', 3: 'Пары ЖЖ', 4: 'Пары ММ' };
const PLATFORM_LABELS = { tg: 'Telegram', max: 'Max', web: 'Веб', anon: 'Без входа' };

const CHART_METRICS = [
    { key: 'impressions', label: 'Показы', format: (v) => formatInt(Math.round(v)) },
    { key: 'clicks', label: 'Клики', format: (v) => formatInt(Math.round(v)) },
    { key: 'ctr', label: 'CTR', format: (v) => `${Number(v).toFixed(2)}%` },
    { key: 'spend', label: 'Расход', format: (v) => `${formatMoney(v)} ₽` },
    { key: 'cpc', label: 'Цена клика', format: (v) => `${formatMoney(v)} ₽` },
];

const money = (value) => `${formatMoney(value)} ₽`;
const percent = (value) => `${Number(value || 0).toFixed(2)}%`;


export default function AdsReportsPage() {
    const { advertiserToken: token } = useAdvertiser();

    const [period, setPeriod] = useState({ period: '30d', dateFrom: null, dateTo: null });
    const [campaignId, setCampaignId] = useState('');
    const [campaignOptions, setCampaignOptions] = useState([]);

    const [summary, setSummary] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [byCampaign, setByCampaign] = useState([]);
    const [byCreative, setByCreative] = useState([]);
    const [byPlacement, setByPlacement] = useState([]);
    const [byGeo, setByGeo] = useState([]);
    const [audience, setAudience] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState(0);
    const [downloading, setDownloading] = useState(null);

    // Список кампаний для фильтра
    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const res = await APIAdsCampaigns(token, { pageSize: 100, hideArchived: false });
                setCampaignOptions(res?.items || []);
            } catch (e) { setCampaignOptions([]); }
        })();
    }, [token]);

    const load = useCallback(async () => {
        if (!token) return;
        const options = { ...period, campaignId: campaignId || undefined };
        try {
            setLoading(true);
            setError(null);
            const [s, t, c, cr, p, g, a] = await Promise.all([
                APIAdsReportSummary(token, options),
                APIAdsReportTimeline(token, options),
                APIAdsReportCampaigns(token, options),
                APIAdsReportCreatives(token, options),
                APIAdsReportPlacements(token, options),
                APIAdsReportGeo(token, { ...options, limit: 100 }),
                APIAdsReportAudience(token, options),
            ]);
            setSummary(s);
            setTimeline(t?.items || []);
            setByCampaign(c?.items || []);
            setByCreative(cr?.items || []);
            setByPlacement(p?.items || []);
            setByGeo(g?.items || []);
            setAudience(a || null);
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось загрузить отчёты'));
        } finally {
            setLoading(false);
        }
    }, [token, period, campaignId]);

    useEffect(() => { load(); }, [load]);

    const download = async (report) => {
        try {
            setDownloading(report);
            const blob = await APIAdsReportExport(token, report, {
                ...period, campaignId: campaignId || undefined,
            });
            saveBlob(blob, `ads_${report}_${period.period || period.dateFrom || 'period'}.csv`);
        } catch (e) {
            setError('Не удалось выгрузить отчёт');
        } finally {
            setDownloading(null);
        }
    };

    const current = summary?.current || {};
    const delta = summary?.delta_percent || {};
    const today = summary?.today || {};

    return (
        <Box>
            <Typography level="h4" sx={{ mb: 0.5 }}>Статистика</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Данные обновляются каждые 10 минут. Блок «Сегодня» — сразу.
            </Typography>

            {error && (
                <Alert color="danger" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="danger" onClick={() => setError(null)}>
                        Скрыть
                    </Button>
                }>{error}</Alert>
            )}

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', lg: 'center' }}
                >
                    <AdPeriodPicker value={period} onChange={setPeriod} />
                    <Select
                        size="sm"
                        value={campaignId}
                        onChange={(_, v) => setCampaignId(v ?? '')}
                        sx={{ minWidth: 240 }}
                    >
                        <Option value="">Все кампании</Option>
                        {campaignOptions.map((c) => (
                            <Option key={c.id} value={String(c.id)}>
                                {c.name} · {CAMPAIGN_STATUS_LABELS[c.status] || c.status}
                            </Option>
                        ))}
                    </Select>
                </Stack>
            </Sheet>

            {/* Сегодня — отдельно и с пометкой: считается напрямую из событий,
                а всё остальное из агрегатов с задержкой */}
            <Sheet variant="soft" color="primary" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
                    <Chip size="sm" variant="solid" color="primary">Сегодня</Chip>
                    <Typography level="body-sm">
                        Показы <b>{formatInt(today.impressions)}</b>
                    </Typography>
                    <Typography level="body-sm">
                        Клики <b>{formatInt(today.clicks)}</b>
                    </Typography>
                    <Typography level="body-sm">
                        CTR <b>{percent(today.ctr)}</b>
                    </Typography>
                    <Typography level="body-sm">
                        Расход <b>{money(today.spend)}</b>
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                        обновляется мгновенно
                    </Typography>
                </Stack>
            </Sheet>

            {loading && !summary ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <AdMetricCard
                            size="lg"
                            label="Показы"
                            value={formatInt(current.impressions)}
                            delta={delta.impressions}
                        />
                        <AdMetricCard
                            size="lg"
                            label="Клики"
                            value={formatInt(current.clicks)}
                            delta={delta.clicks}
                        />
                        <AdMetricCard
                            label="CTR"
                            value={percent(current.ctr)}
                            delta={delta.ctr}
                        />
                        <AdMetricCard
                            label="Расход"
                            value={money(current.spend)}
                            delta={delta.spend}
                            hint="начислено"
                        />
                        <AdMetricCard
                            label="Цена клика"
                            value={money(current.cpc)}
                            delta={delta.cpc}
                            inverted
                        />
                        <AdMetricCard
                            label="eCPM"
                            value={money(current.ecpm)}
                        />
                    </Stack>

                    {(current.clicks_suspicious > 0 || current.impressions_suspicious > 0) && (
                        <Alert color="neutral" variant="soft" size="sm" sx={{ mb: 2 }}>
                            Отфильтровано как недействительные: показов{' '}
                            {formatInt(current.impressions_suspicious)}, кликов{' '}
                            {formatInt(current.clicks_suspicious)}. Они не учтены в
                            статистике выше и не были оплачены.
                        </Alert>
                    )}

                    <Box sx={{ mb: 3 }}>
                        <AdLineChart data={timeline} metrics={CHART_METRICS} />
                    </Box>

                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'transparent' }}>
                        <TabList sx={{
                            mb: 2, borderRadius: 'md', overflowX: 'auto', flexWrap: 'nowrap',
                            [`& .${tabClasses.root}`]: { whiteSpace: 'nowrap', flexShrink: 0 },
                            [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                                bgcolor: 'primary.softBg', color: 'primary.700',
                            },
                        }}>
                            <Tab>Кампании</Tab>
                            <Tab>Объявления</Tab>
                            <Tab>Площадки</Tab>
                            <Tab>География</Tab>
                            <Tab>Аудитория</Tab>
                        </TabList>

                        <TabPanel value={0} sx={{ p: 0 }}>
                            <ExportButton
                                report="campaigns"
                                downloading={downloading}
                                onClick={download}
                            />
                            <AdBreakdownTable
                                defaultSort="spend"
                                rows={byCampaign}
                                columns={[
                                    { key: 'name', label: 'Кампания', width: 260 },
                                    {
                                        key: 'status', label: 'Статус', width: 130,
                                        render: (r) => CAMPAIGN_STATUS_LABELS[r.status] || r.status,
                                    },
                                    {
                                        key: 'impressions', label: 'Показы', align: 'right', width: 110,
                                        render: (r) => formatInt(r.impressions),
                                        total: (t) => formatInt(t.impressions),
                                    },
                                    {
                                        key: 'clicks', label: 'Клики', align: 'right', width: 100,
                                        render: (r) => formatInt(r.clicks),
                                        total: (t) => formatInt(t.clicks),
                                    },
                                    {
                                        key: 'ctr', label: 'CTR', align: 'right', width: 90,
                                        render: (r) => percent(r.ctr),
                                        total: (t) => percent(t.ctr),
                                    },
                                    {
                                        key: 'spend', label: 'Расход', align: 'right', width: 120,
                                        render: (r) => money(r.spend),
                                        total: (t) => money(t.spend),
                                    },
                                    {
                                        key: 'cpc', label: 'Цена клика', align: 'right', width: 120,
                                        render: (r) => money(r.cpc),
                                        total: (t) => money(t.cpc),
                                    },
                                ]}
                            />
                        </TabPanel>

                        <TabPanel value={1} sx={{ p: 0 }}>
                            <ExportButton
                                report="creatives"
                                downloading={downloading}
                                onClick={download}
                            />
                            <AdBreakdownTable
                                defaultSort="impressions"
                                rows={byCreative}
                                columns={[
                                    { key: 'title', label: 'Объявление', width: 260 },
                                    {
                                        key: 'format', label: 'Формат', width: 160,
                                        render: (r) => FORMAT_LABELS[r.format] || r.format,
                                    },
                                    {
                                        key: 'impressions', label: 'Показы', align: 'right', width: 110,
                                        render: (r) => formatInt(r.impressions),
                                        total: (t) => formatInt(t.impressions),
                                    },
                                    {
                                        key: 'clicks', label: 'Клики', align: 'right', width: 100,
                                        render: (r) => formatInt(r.clicks),
                                        total: (t) => formatInt(t.clicks),
                                    },
                                    {
                                        key: 'ctr', label: 'CTR', align: 'right', width: 90,
                                        render: (r) => percent(r.ctr),
                                        total: (t) => percent(t.ctr),
                                    },
                                    {
                                        key: 'video_completion_rate', label: 'Досмотры',
                                        align: 'right', width: 110,
                                        render: (r) => (r.video_completion_rate
                                            ? percent(r.video_completion_rate) : '—'),
                                    },
                                    {
                                        key: 'spend', label: 'Расход', align: 'right', width: 120,
                                        render: (r) => money(r.spend),
                                        total: (t) => money(t.spend),
                                    },
                                ]}
                            />
                        </TabPanel>

                        <TabPanel value={2} sx={{ p: 0 }}>
                            <ExportButton
                                report="placements"
                                downloading={downloading}
                                onClick={download}
                            />
                            <AdBreakdownTable
                                defaultSort="impressions"
                                rows={byPlacement}
                                columns={[
                                    { key: 'name', label: 'Площадка', width: 240 },
                                    {
                                        key: 'impressions', label: 'Показы', align: 'right', width: 110,
                                        render: (r) => formatInt(r.impressions),
                                        total: (t) => formatInt(t.impressions),
                                    },
                                    {
                                        key: 'clicks', label: 'Клики', align: 'right', width: 100,
                                        render: (r) => formatInt(r.clicks),
                                        total: (t) => formatInt(t.clicks),
                                    },
                                    {
                                        key: 'ctr', label: 'CTR', align: 'right', width: 90,
                                        render: (r) => percent(r.ctr),
                                        total: (t) => percent(t.ctr),
                                    },
                                    {
                                        key: 'ecpm', label: 'eCPM', align: 'right', width: 110,
                                        render: (r) => money(r.ecpm),
                                        total: (t) => money(t.ecpm),
                                    },
                                    {
                                        key: 'spend', label: 'Расход', align: 'right', width: 120,
                                        render: (r) => money(r.spend),
                                        total: (t) => money(t.spend),
                                    },
                                ]}
                            />
                        </TabPanel>

                        <TabPanel value={3} sx={{ p: 0 }}>
                            <ExportButton report="geo" downloading={downloading} onClick={download} />
                            <AdBreakdownTable
                                defaultSort="impressions"
                                maxHeight={520}
                                rows={byGeo}
                                columns={[
                                    {
                                        key: 'city', label: 'Город', width: 220,
                                        render: (r) => r.city || 'Не указан',
                                    },
                                    { key: 'region', label: 'Регион', width: 200 },
                                    {
                                        key: 'impressions', label: 'Показы', align: 'right', width: 110,
                                        render: (r) => formatInt(r.impressions),
                                        total: (t) => formatInt(t.impressions),
                                    },
                                    {
                                        key: 'clicks', label: 'Клики', align: 'right', width: 100,
                                        render: (r) => formatInt(r.clicks),
                                        total: (t) => formatInt(t.clicks),
                                    },
                                    {
                                        key: 'ctr', label: 'CTR', align: 'right', width: 90,
                                        render: (r) => percent(r.ctr),
                                        total: (t) => percent(t.ctr),
                                    },
                                    {
                                        key: 'spend', label: 'Расход', align: 'right', width: 120,
                                        render: (r) => money(r.spend),
                                        total: (t) => money(t.spend),
                                    },
                                ]}
                            />
                        </TabPanel>

                        <TabPanel value={4} sx={{ p: 0 }}>
                            <Stack spacing={2}>
                                <AudienceBlock
                                    title="По полу"
                                    rows={audience?.by_gender}
                                    labelKey="gender"
                                    labels={GENDER_LABELS}
                                />
                                <AudienceBlock
                                    title="По возрасту"
                                    rows={audience?.by_age}
                                    labelKey="age_bucket"
                                />
                                <AudienceBlock
                                    title="По платформе"
                                    rows={audience?.by_platform}
                                    labelKey="platform"
                                    labels={PLATFORM_LABELS}
                                />
                            </Stack>
                        </TabPanel>
                    </Tabs>
                </>
            )}
        </Box>
    );
}


function ExportButton({ report, downloading, onClick }) {
    return (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<DownloadRoundedIcon />}
                loading={downloading === report}
                onClick={() => onClick(report)}
            >
                Скачать CSV
            </Button>
        </Stack>
    );
}


/**
 * Демографический разрез. Доля считается от суммы показов внутри блока,
 * а не от общего числа: пользователи без указанного пола образуют
 * отдельную строку, и без нормировки проценты не сойдутся к сотне.
 */
function AudienceBlock({ title, rows, labelKey, labels }) {
    const total = (rows || []).reduce((sum, r) => sum + (Number(r.impressions) || 0), 0);

    if (!rows?.length) {
        return (
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                <Typography level="title-sm" sx={{ mb: 0.5 }}>{title}</Typography>
                <Typography level="body-sm" color="neutral">Данных за период нет</Typography>
            </Sheet>
        );
    }

    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
            <Typography level="title-sm" sx={{ mb: 1.5 }}>{title}</Typography>
            <Stack spacing={1.2}>
                {rows.map((row, index) => {
                    const raw = row[labelKey];
                    const label = labels?.[raw] ?? (raw === null || raw === undefined
                        ? 'Не указано' : String(raw));
                    const share = total ? (Number(row.impressions) / total) * 100 : 0;
                    return (
                        <Box key={`${raw}-${index}`}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
                                <Typography level="body-sm">{label}</Typography>
                                <Typography level="body-sm" color="neutral">
                                    {formatInt(row.impressions)} показов ·{' '}
                                    CTR {percent(row.ctr)} · {money(row.spend)}
                                </Typography>
                            </Stack>
                            <Box sx={{ height: 6, borderRadius: 6, bgcolor: 'neutral.softBg' }}>
                                <Box sx={{
                                    width: `${share}%`, height: '100%',
                                    borderRadius: 6, bgcolor: 'primary.400',
                                }} />
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </Sheet>
    );
}